// Everything that touches the host page lives here.
//
// Wikinggruppen renders custom product fields as
//   <div class="product-custom-fields"><div class="input-form__row">
//     <label for="custom-field-0">image-path</label>
//     <input class="js-custom-text-field" type="text" name="custom-text0" id="custom-field-0">
//   </div></div>
// The field is found by its label text ("image-path") rather than by name, because the
// index in custom-text0 shifts if the product gets more custom fields. The row is hidden:
// the customer never types there. Override with targetInputSelector if the label changes.
//
// The buy button is <button type="submit" class="btn ... js-product-page-add">; it stays disabled
// until a design is saved and is disabled again on "Börja om". While disabled it carries a title
// explaining why — also on its wrapper, because a theme that sets pointer-events:none on
// :disabled swallows the tooltip on the button itself. Any title the theme set is restored.

export const FIELD_LABEL = 'image-path';
export const BUY_BUTTON = 'button.js-product-page-add';

export interface HostHooks {
  setReference(reference: string | null): void;
}

function tooltip(el: HTMLElement | null, text: string | null): void {
  if (!el) return;
  if (text) {
    if (el.dataset.dcTitle === undefined) el.dataset.dcTitle = el.getAttribute('title') ?? '';
    el.setAttribute('title', text);
  } else if (el.dataset.dcTitle !== undefined) {
    const original = el.dataset.dcTitle;
    delete el.dataset.dcTitle;
    if (original) el.setAttribute('title', original);
    else el.removeAttribute('title');
  }
}

function byLabel(scope: ParentNode): HTMLInputElement | null {
  const label = [...scope.querySelectorAll('label')].find((l) => l.textContent?.trim() === FIELD_LABEL);
  const input = label?.htmlFor ? scope.querySelector<HTMLInputElement>(`#${CSS.escape(label.htmlFor)}`) : null;
  if (input) (input.closest('.input-form__row') as HTMLElement | null)?.style.setProperty('display', 'none');
  return input;
}

export function attachHost(
  mountEl: HTMLElement,
  targetInputSelector?: string,
  addToCartSelector?: string,
  blockedTitle?: string,
  onBuy?: () => void,
): HostHooks {
  const scope: ParentNode = mountEl.closest('form') ?? document;
  const find = <T extends Element>(sel?: string) => (sel ? (scope.querySelector<T>(sel) ?? document.querySelector<T>(sel)) : null);
  const input = find<HTMLInputElement | HTMLTextAreaElement>(targetInputSelector) ?? byLabel(scope) ?? byLabel(document);
  const button = find<HTMLButtonElement>(addToCartSelector ?? BUY_BUTTON);
  if (!input) console.warn(`dc-cake-editor: no "${FIELD_LABEL}" field found; the reference will not reach the cart`);
  // Capture phase: the theme's own add-to-cart handler may stop propagation. A disabled button fires no click.
  if (button && onBuy) button.addEventListener('click', onBuy, true);
  return {
    setReference(reference) {
      if (input) {
        input.value = reference ?? '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (button) {
        button.disabled = !reference;
        const why = reference || !blockedTitle ? null : blockedTitle;
        tooltip(button, why);
        tooltip(button.parentElement, why);
      }
    },
  };
}

const KEY = (productId: string) => `dc-cake-editor:${productId}`;

export function rememberReference(productId: string, reference: string | null): void {
  try {
    if (reference) sessionStorage.setItem(KEY(productId), reference);
    else sessionStorage.removeItem(KEY(productId));
  } catch { /* private mode etc. — restore simply won't work */ }
}

export function recallReference(productId: string): string | null {
  try { return sessionStorage.getItem(KEY(productId)); } catch { return null; }
}
